import { useMemo, useState, useCallback } from 'react';
import { mergeClasses } from '@fluentui/react-components';
import { FilterRegular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { BasicButton } from '@components/common/BasicButton';
import { CalendarScreen } from '@/screens/CalendarScreen';
import { EventFilterPanel, EMPTY_EVENT_FILTERS } from '@/layouts/EventFilterPanel';
import type { EventFilters, BuildingOption } from '@/layouts/EventFilterPanel';
import { useGetBuildingsQuery } from '@/store/api/spaceConfigService/spaceConfigServiceApi';
import { useCalendarPanelStyles } from './CalendarPanel.styles';

/**
 * Self-contained calendar panel: manages the multi-field event filter UI
 * and passes filters to CalendarScreen which handles the API call with
 * date range + filters combined.
 */
export const CalendarPanel = () => {
  const styles = useCalendarPanelStyles();
  const { t } = useTranslation('common');
  const [eventFilters, setEventFilters] = useState<EventFilters>(EMPTY_EVENT_FILTERS);
  const [isEventFilterOpen, setIsEventFilterOpen] = useState(false);

  const hasActiveFilters =
    eventFilters.statuses.length > 0 ||
    eventFilters.buildingIds.length > 0 ||
    eventFilters.ownerEmails.length > 0;

  // Calendar must never serve stale server data — always refetch on mount,
  // focus, or reconnect.
  const { data: buildingsResponse } = useGetBuildingsQuery(undefined, {
    refetchOnMountOrArgChange: true,
    refetchOnReconnect: true,
  });

  const buildingOptions = useMemo<BuildingOption[]>(() => {
    if (!buildingsResponse || !buildingsResponse.success) return [];
    const buildings = buildingsResponse.data;
    if (!Array.isArray(buildings)) return [];
    return buildings.map((b) => ({
      buildingId: b.buildingId as string,
      buildingName: b.buildingName,
    }));
  }, [buildingsResponse]);

  const handleApplyEventFilters = useCallback((filters: EventFilters) => {
    setEventFilters(filters);
    setIsEventFilterOpen(false);
  }, []);

  const handleClearFilters = useCallback(() => {
    setEventFilters(EMPTY_EVENT_FILTERS);
  }, []);

  const handleCancelEventFilter = useCallback(() => {
    setIsEventFilterOpen(false);
  }, []);

  return (
    <>
      <CalendarScreen
        eventFilters={eventFilters}
        onClearFilters={handleClearFilters}
        eventsFilterSlot={
          <BasicButton
            variant="outline"
            size="small"
            icon={<FilterRegular />}
            className={mergeClasses(
              styles.filterButton,
              hasActiveFilters && styles.filterButtonActive
            )}
            onClick={() => setIsEventFilterOpen(true)}
            data-testid="open-event-filter-panel"
          >
            {t('calendar.filter.label')}
          </BasicButton>
        }
      />
      <EventFilterPanel
        open={isEventFilterOpen}
        appliedFilters={eventFilters}
        buildingOptions={buildingOptions}
        onApply={handleApplyEventFilters}
        onCancel={handleCancelEventFilter}
      />
    </>
  );
};

CalendarPanel.displayName = 'CalendarPanel';
